import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { initStatusBar } from "@/native/capacitor";
import { initDeepLinks, popPendingDeepLink } from "@/native/deeplink";
import { initPushListeners, setPushRouteHandler, registerIfGranted } from "@/native/push";
import { AppDataProvider } from "@/context/AppDataContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";
import Login from "@/pages/Login";
import Today from "@/pages/Today";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import MapPage from "@/pages/MapPage";
import Trips from "@/pages/Trips";
import More from "@/pages/More";
import UsersPage from "@/pages/Users";
import AuditPage from "@/pages/Audit";
import Discover from "@/pages/Discover";
import ReviewQueue from "@/pages/ReviewQueue";
import Availability from "@/pages/Availability";
import Notifications from "@/pages/Notifications";
import NotificationPreferences from "@/pages/NotificationPreferences";
import Team from "@/pages/Team";
import Territories from "@/pages/Territories";
import Hotspots from "@/pages/Hotspots";
import MonitoredAreas from "@/pages/MonitoredAreas";
import WebsiteIntentAdmin from "@/pages/WebsiteIntentAdmin";
import VisitorInbox from "@/pages/VisitorInbox";
import AdminDiagnostics from "@/pages/AdminDiagnostics";
import WebsiteKits from "@/pages/WebsiteKits";
import CompaniesHouseReviews from "@/pages/CompaniesHouseReviews";
import PilotDashboard from "@/pages/PilotDashboard";
import AcceptInvitation from "@/pages/AcceptInvitation";

function AdminRoute({ children }) {
  const { isAdmin, user } = useAuth();
  if (user === undefined) return null;
  return isAdmin ? children : <Navigate to="/today" replace />;
}

function ManagerRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  const ok = ["sales_manager", "admin", "super_admin"].includes(user?.role);
  return ok ? children : <Navigate to="/today" replace />;
}

function Shell() {
  return (
    <AppDataProvider>
      <AppShell>
        <Routes>
          <Route path="/today" element={<Today />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/leads/:id" element={<LeadDetail />} />
          <Route path="/availability" element={<Availability />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/notification-preferences" element={<NotificationPreferences />} />
          <Route path="/team" element={<ManagerRoute><Team /></ManagerRoute>} />
          <Route path="/territories" element={<ManagerRoute><Territories /></ManagerRoute>} />
          <Route path="/monitored-areas" element={<ManagerRoute><MonitoredAreas /></ManagerRoute>} />
          <Route path="/trips" element={<Trips />} />
          <Route path="/hotspots" element={<Hotspots />} />
          <Route path="/visitors" element={<ManagerRoute><VisitorInbox /></ManagerRoute>} />
          <Route path="/admin/website-intent" element={<AdminRoute><WebsiteIntentAdmin /></AdminRoute>} />
          <Route path="/admin/diagnostics/webintent" element={<AdminRoute><AdminDiagnostics /></AdminRoute>} />
          <Route path="/admin/website-kits" element={<AdminRoute><WebsiteKits /></AdminRoute>} />
          <Route path="/admin/companies-house" element={<ManagerRoute><CompaniesHouseReviews /></ManagerRoute>} />
          <Route path="/admin/pilot" element={<ManagerRoute><PilotDashboard /></ManagerRoute>} />
          <Route path="/more" element={<More />} />
          <Route path="/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
          <Route path="/audit" element={<AdminRoute><AuditPage /></AdminRoute>} />
          <Route path="/reviews" element={<ManagerRoute><ReviewQueue /></ManagerRoute>} />
          <Route path="*" element={<Navigate to="/today" replace />} />
        </Routes>
      </AppShell>
    </AppDataProvider>
  );
}

// Native bootstrap: status bar, deep links and push listeners (all no-op on web). Routes push
// taps / universal links through React Router, and resumes any pending deep link after login.
function NativeBootstrap() {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    initStatusBar();
    setPushRouteHandler((path) => navigate(path));
    initDeepLinks((path) => navigate(path));
    initPushListeners();
  }, [navigate]);
  useEffect(() => {
    if (user) {
      registerIfGranted();  // silent token refresh/rotation; never prompts
      const pending = popPendingDeepLink();
      if (pending) navigate(pending);
    }
  }, [user, navigate]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <NativeBootstrap />
          <Toaster position="top-center" richColors offset="72px" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Shell />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
